import { normalizeApiError } from "@/shared/api/client";
import type { PublicWebCopyKey } from "@/shared/i18n/public-web-copy";

type AuthCopy = (
  key: PublicWebCopyKey,
  vars?: Record<string, string | number | undefined | null>,
) => string;

const AUTH_ERROR_COPY_KEYS: Readonly<Record<string, PublicWebCopyKey>> = {
  invalid_credentials: "auth.error.invalidCredentials",
  account_locked: "auth.error.accountLocked",
  missing_id_token: "auth.error.googleVerification",
  invalid_google_token: "auth.error.googleVerification",
  google_not_configured: "auth.error.googleNotConfigured",
  google_email_required: "auth.error.googleEmailRequired",
  email_taken: "auth.error.emailTaken",
  phone_taken: "auth.error.phoneTaken",
  current_contact_verification_required: "auth.error.currentContactVerification",
  invalid_identity_change_response: "auth.error.invalidIdentityChange",
  invalid_current_contact_verification_response: "auth.error.invalidCurrentContact",
  identity_change_user_mismatch: "auth.error.identityChanged",
  invalid_otp: "auth.error.invalidOtp",
  otp_target_mismatch: "auth.error.otpTargetMismatch",
  invalid_contact_sign_in_challenge: "auth.error.challengeExpired",
  contact_sign_in_challenge_expired: "auth.error.challengeExpired",
  credential_version_mismatch: "auth.error.credentialChanged",
  verification_in_progress: "auth.error.verificationInProgress",
  contact_sign_in_device_mismatch: "auth.error.deviceMismatch",
  contact_already_verified: "auth.error.alreadyVerified",
  unsupported_delivery_channel: "auth.error.unsupportedDelivery",
  invalid_contact_sign_in_response: "auth.error.invalidContactResponse",
  contact_sign_in_challenge_write_failed: "auth.error.challengeStart",
  invalid_auth_token_response: "auth.error.invalidTokenResponse",
  invalid_auth_session_response: "auth.error.invalidSessionResponse",
  user_not_found: "auth.error.userNotFound",
  auth_session_mutation_superseded: "auth.error.sessionChanged",
  rate_limited: "auth.error.rateLimited",
  authentication_state_write_failed: "auth.error.stateWrite",
  invalid_account_creation_response: "auth.error.invalidAccountCreation",
  auth_tokens_missing: "auth.error.invalidAccountCreation",
  passkey_cancelled: "auth.error.passkeyCancelled",
  invalid_password_reset_delivery_response: "auth.error.resetDeliveryUnsafe",
};

export function localizedAuthError(error: unknown, copy: AuthCopy): string {
  const normalized = normalizeApiError(error);
  if (normalized.code === "device_bound_to_other_account") {
    const body =
      normalized.body && typeof normalized.body === "object"
        ? (normalized.body as Record<string, unknown>)
        : {};
    const masked =
      typeof body.deviceOwnerMasked === "string" && body.deviceOwnerMasked.trim()
        ? body.deviceOwnerMasked
        : copy("auth.error.anotherAccount");
    return copy("auth.error.deviceBound", { account: masked });
  }
  const key = AUTH_ERROR_COPY_KEYS[normalized.code || ""];
  return key ? copy(key) : copy("auth.error.generic");
}
